import type {
  DataSource,
  EntityPluginContext,
  EntityTypeConfig,
  JobHandler,
  Plugin,
  Template,
} from "@brains/plugins";
import { EntityPlugin, emptyEntityPluginConfigSchema } from "@brains/plugins";
import { AgentAdapter } from "../adapters/agent-adapter";
import { AgentDataSource } from "../datasources/agent-datasource";
import { ProximityMapDataSource } from "../datasources/proximity-map-datasource";
import { AgentGenerationJobHandler } from "../handlers/agent-generation-handler";
import { registerAgentNetworkDashboardWidget } from "../lib/agent-dashboard";
import {
  refreshKnownAgentCards,
  registerAtprotoBrainCardHandlers,
  type AtprotoCardFetch,
} from "../lib/atproto-card-events";
import type { ResolveHostname } from "@brains/utils/safe-public-fetch";
import { getAgentDiscoveryInstructions } from "../lib/agent-instructions";
import { AGENT_DISCOVERY_PLUGIN_ID, AGENT_ENTITY_TYPE } from "../lib/constants";
import { getTemplates } from "../lib/register-templates";
import { agentEntitySchema, type AgentEntity } from "../schemas/agent";
import packageJson from "../../package.json";

const agentAdapter: AgentAdapter = new AgentAdapter();

export class AgentDiscoveryPlugin extends EntityPlugin<
  AgentEntity,
  Record<string, never>,
  Record<string, never>
> {
  readonly entityType: typeof AGENT_ENTITY_TYPE = AGENT_ENTITY_TYPE;
  readonly schema: typeof agentEntitySchema = agentEntitySchema;
  readonly adapter: AgentAdapter = agentAdapter;
  private readonly fetchFn: AtprotoCardFetch | undefined;
  private readonly resolveHostname: ResolveHostname | undefined;

  constructor(fetchFn?: AtprotoCardFetch, resolveHostname?: ResolveHostname) {
    super(
      AGENT_DISCOVERY_PLUGIN_ID,
      packageJson,
      {},
      emptyEntityPluginConfigSchema,
    );
    this.fetchFn = fetchFn;
    this.resolveHostname = resolveHostname;
  }

  protected override getEntityTypeConfig(): EntityTypeConfig | undefined {
    return {
      classification: "system",
      projectionSourceRole: "supporting",
      // Nothing reads these; did and a2aEndpoint duplicate brainDid and url.
      retiredFields: [
        "cardObservedAt",
        "cardLastCheckedAt",
        "a2aEndpoint",
        "did",
        "anchorDid",
      ],
    };
  }

  protected override createGenerationHandler(
    context: EntityPluginContext,
  ): JobHandler | null {
    return new AgentGenerationJobHandler(
      this.logger.child("AgentGenerationJobHandler"),
      context,
    );
  }

  protected override getTemplates(): Record<string, Template> {
    return getTemplates();
  }

  protected override getDataSources(): DataSource[] {
    return [
      new AgentDataSource(this.logger.child("AgentDataSource")),
      new ProximityMapDataSource(),
    ];
  }

  protected override async onRegister(
    context: EntityPluginContext,
  ): Promise<void> {
    // Brains no longer copy what other brains publish; delete the pieces a
    // brain kept before, with their files.
    await context.entityService.purgeEntityType("network-piece");
    registerAtprotoBrainCardHandlers(context);
    context.recurringChecks.register({
      id: "agent-card-refresh",
      cadence: "daily",
      deliverAlerts: false,
      run: async ({ signal }) => {
        await refreshKnownAgentCards(
          context,
          this.fetchFn,
          signal,
          new Date().toISOString(),
          this.resolveHostname,
        );
        return {};
      },
    });
    registerAgentNetworkDashboardWidget(context);
  }

  protected override async getInstructions(): Promise<string> {
    return [
      getAgentDiscoveryInstructions(),
      'Agent approval/archive is a status field update on `entityType: "agent"`.',
    ].join("\n");
  }
}

export function agentDiscoveryPlugin(): Plugin {
  return new AgentDiscoveryPlugin();
}
